// The Clarus Partners advertising plan, rendered in /admin and printable to PDF.
// Structured as sections of typed blocks the admin page renders.

export const AD_PLAN_META = {
  title: "Clarus Partners - Website Audit & Advertising Plan",
  subtitle: "Goal: $5k/month revenue within 3 months · 10-20 hrs/week · solo",
  updated: "Prepared at site rebuild",
};

export const AD_PLAN = [
  {
    id: "audit",
    title: "1. Website audit - what we found and fixed",
    blocks: [
      { type: "p", text: "Audit of the previous single-page site (WordPress + Elementor) at claruspartners.com.au, and what the rebuild addresses." },
      { type: "h3", text: "Quick wins (fixed in the rebuild)" },
      {
        type: "list",
        items: [
          'Title tag was "Clarus Partners - Clarus Partners" - now "Xero Bookkeeper for Trades & Startups | Clarus Partners", written for search intent.',
          'CTA said "30 minute consultation" but the Calendly event is "30min" - copy standardised to 30 minutes everywhere. ACTION: rename the Calendly event to a 30-minute consultation to match (or tell me and the copy flips to 30).',
          "EasyCars / automotive references removed - they clashed with the chosen niches and the conflict-of-interest constraint.",
          "One H1, logical H2/H3 structure, meta description written for click-through, LocalBusiness-style ProfessionalService + FAQ structured data, sitemap.xml and robots.txt added.",
        ],
      },
      { type: "h3", text: "Bigger changes (built into the new site)" },
      {
        type: "list",
        items: [
          "Generic services replaced with five productised packages (Health Check, Clean-Up Sprint, Monthly Bookkeeping + Reporting, Fractional CFO, Business Setup), each with inclusions, who it's for, timeframe and from-pricing placeholders.",
          'Added an "Is this you?" pain-point section so trades businesses, startups and service businesses recognise themselves in the first 30 seconds.',
          "Added FAQ (cost, turnaround, Xero vs MYOB, remote work, data security, tax lodging) - removes the objections that stop people booking.",
          "Added a second conversion path: free Xero Health Check Checklist (email opt-in) for visitors not ready to book, plus a quote request form.",
          "Trust gaps flagged: photo of Nima, real testimonials, pricing, ABN and Xero-certified badge are all marked [NIMA TO PROVIDE] - the single biggest conversion lever left is replacing these placeholders with real proof.",
        ],
      },
    ],
  },
  {
    id: "channels",
    title: "2. Recommended channels (and why)",
    blocks: [
      { type: "p", text: "Budget assumption: $500-$1,000/month to start, solo operator. That rules out spreading thin - one paid channel done properly beats three done badly." },
      {
        type: "table",
        headers: ["Channel", "Role", "Why", "Budget"],
        rows: [
          ["Google Search Ads", "Primary paid", 'People searching "xero bookkeeper for trades" or "xero clean up" already have the problem and money to fix it. Highest intent available to you.', "$500-$750/mo to start"],
          ["LinkedIn organic + Google Business Profile + referrals", "Supporting (free)", "Builds trust and feeds the same funnel while paid ramps up. Costs time, not money.", "Free - 2-3 hrs/week"],
          ["Meta Ads (Facebook/Instagram)", "Later test only", "Good for trades audiences but interrupt-based, lower intent. Test in month 3 only if Google is converting.", "$0 now; $300/mo test later"],
        ],
      },
      { type: "callout", text: "Not recommended yet: SEO content (slow payback, start month 2+), TikTok/YouTube (wrong buying intent for B2B finance), print/local sponsorships." },
    ],
  },
  {
    id: "google-ads",
    title: "3. Google Ads kit",
    blocks: [
      { type: "h3", text: "Campaign structure" },
      { type: "p", text: "One Search campaign, three ad groups: (1) Xero clean-up, (2) bookkeeper for trades, (3) fractional CFO / outsourced finance. Start with phrase match only. Send all traffic to /xero-health-check (the checklist landing page) - it converts cold clicks into email leads you can follow up, which suits a free-diagnostic offer better than a hard booking ask." },
      { type: "h3", text: "Target keywords (phrase match)" },
      {
        type: "list",
        items: [
          '"xero clean up", "xero cleanup", "xero catch up bookkeeping"',
          '"xero bookkeeper for trades", "bookkeeper for trades", "trades bookkeeper australia"',
          '"xero bookkeeper australia", "remote bookkeeper australia", "virtual bookkeeper australia"',
          '"fractional cfo australia", "fractional cfo small business", "outsourced cfo"',
          '"xero health check", "fix my xero file", "xero reconciliation help"',
          '"bookkeeper for startups", "startup bookkeeping australia"',
        ],
      },
      { type: "h3", text: "Negative keywords (add on day one)" },
      {
        type: "list",
        items: [
          "free, jobs, course, salary, template, DIY, how to",
          "car dealership, automotive, dealer (conflict of interest)",
          "not for profit, nonprofit, charity, kindergarten, childcare",
          "myob (unless you want switchers - test later), quickbooks, reckon",
        ],
      },
      { type: "h3", text: "Ad variations (Responsive Search Ad assets)" },
      {
        type: "table",
        headers: ["#", "Headlines (max 30 chars)", "Descriptions (max 90 chars)"],
        rows: [
          ["1", "Messy Xero File? Fixed Fast | CPA-Led Xero Clean-Ups | Free Xero Health Check", "Months behind on your books? A CPA fixes your Xero file so your accountant can lodge. Free health check. | Fixed-scope quote before work starts. No hourly meters. Remote, Australia-wide."],
          ["2", "Bookkeeper For Trades | Xero Done For You | Nights Back, Books Sorted", "Built for trades businesses: invoicing, bank recs, payroll and accountant-ready books handled for you. | Free 30-min consult with a CPA. Fixed pricing. No lock-in."],
          ["3", "Fractional CFO For Startups | Investor-Ready Numbers | From A CPA, Not A Junior", "Burn rate, runway, board reports - senior finance help a few days a month. | 15+ years incl. Finance Director roles. Remote across Australia."],
          ["4", "Behind On Your Xero? | Fixed-Price Clean-Up Sprints | Hand Your Accountant A Clean File", "We fix historical recs, errors and suspense items so lodgement is painless. | Free health check first. Fixed quote. No surprises."],
          ["5", "Xero Bookkeeper Australia | CPA-Led, Remote, Fixed Price | Free 30-Min Consult", "Trades, startups & service businesses: get your books out of the red zone. | Health check, clean-up, then monthly tidy books. Book a free call."],
        ],
      },
      { type: "h3", text: "Landing page outline (already built at /xero-health-check)" },
      {
        type: "list",
        items: [
          "Headline matches the ad promise: is your Xero file lodgement-ready?",
          "Single primary action: get the free checklist (email opt-in) - low friction for cold traffic",
          "Secondary path: book the free 30-minute consult for ready buyers",
          "Proof and CPA credentials below the fold, FAQ to catch objections",
          "Conversion tracking fires on checklist download and booking click",
        ],
      },
    ],
  },
  {
    id: "tracking",
    title: "4. Tracking setup & what to measure",
    blocks: [
      { type: "h3", text: "What's wired on this site" },
      {
        type: "list",
        items: [
          "Three conversion events are already coded: book_consultation_click, checklist_download, quote_form_submit.",
          "They fire to GA4, Google Ads (as conversions) and Meta Pixel automatically once IDs are entered in Admin → Tracking.",
          "Google Search Console verification tag field is also in Admin → Tracking.",
        ],
      },
      { type: "h3", text: "To activate (takes ~30 minutes)" },
      {
        type: "list",
        items: [
          "Create a GA4 property at analytics.google.com → copy the Measurement ID (G-XXXXXXX) into Admin → Tracking.",
          "Create a Google Ads account → Tools → Conversions → note the Conversion ID (AW-XXXXXXX) and label → paste into Admin → Tracking.",
          "Create a Meta Pixel in Events Manager → copy the Pixel ID into Admin → Tracking (only when you start Meta ads).",
          "Verify the domain in Google Search Console → paste the verification token into Admin → Tracking → submit sitemap.xml.",
        ],
      },
      { type: "h3", text: "The three numbers that matter" },
      {
        type: "table",
        headers: ["Metric", "Formula", "Healthy target"],
        rows: [
          ["Cost per lead", "Ad spend ÷ (checklist downloads + quote forms)", "Under $40-60"],
          ["Booking rate", "Booked calls ÷ leads", "25%+ (follow up fast)"],
          ["Cost per client", "Ad spend ÷ new paying clients", "Under ~$500 (pays back in month 1-2 of a monthly package)"],
        ],
      },
      { type: "callout", text: "At $5k/month goal with ~$1k/mo average client value, you need roughly 5 clients. At a $500 cost per client that is ~$2,500 of ad spend total - the maths works if booking rate stays above 25%, which is why fast follow-up on every lead matters more than more traffic." },
    ],
  },
  {
    id: "90-day",
    title: "5. The 90-day plan",
    blocks: [
      {
        type: "table",
        headers: ["Weeks", "Focus", "Actions"],
        rows: [
          ["1-2", "Launch", "Cut over domain, submit sitemap to Search Console, launch Google Ads at $25/day, publish Google Business Profile, post 2× on LinkedIn announcing the niche focus."],
          ["3-4", "Learn", "Review search terms report twice a week, add negatives ruthlessly, pause keywords with spend and no leads. Follow up every checklist lead within 24 hours with a personal email."],
          ["5-8", "Optimise", "Shift budget to the ad group producing the cheapest leads. Test one new ad variation per fortnight. Ask every client and happy enquirer for a Google review and one referral."],
          ["9-12", "Scale", "If cost per client is under $500, raise daily budget by 30-50%. Start the Meta test only if Google is stable. Publish first 2 SEO articles targeting trades Xero questions."],
          ["13", "Review", "Compare cost per client vs target, decide whether to scale to $1,500/mo or hold. Document which niche converts best and double down."],
        ],
      },
      { type: "h3", text: "Lean organic plan (runs alongside, ~2-3 hrs/week)" },
      {
        type: "list",
        items: [
          "LinkedIn: 2 posts/week. Alternate: (a) a plain-English Xero tip for trades businesses and startups, (b) a short story from your finance career. End every post with the free checklist link.",
          "Google Business Profile: set up as a service-area business (Australia-wide), collect reviews from day one - even 3-5 reviews changes conversion.",
          "Referral asks: after every successful clean-up, ask the client 'who else do you know whose books are a mess?' - and ask 2-3 tax accountants if they want a clean-file partner to send messy Xero files to. Accountants referring clean-up work to you is the highest-quality channel available and costs nothing.",
        ],
      },
    ],
  },
  {
    id: "compliance",
    title: "6. Australian compliance basics",
    blocks: [
      {
        type: "list",
        items: [
          "Spam Act 2003: every marketing email needs consent (the checklist opt-in and quote form count as consent for follow-up about their enquiry), must identify Clarus Partners as the sender, and must include a working unsubscribe. Transactional replies to enquiries are fine; newsletters need clear consent.",
          "Privacy Act / Australian Privacy Principles: only collect what you need (name, email, business details), tell people why, store it securely, and publish a privacy policy - done at /privacy. Add your ABN before launch.",
          "ACCC - no misleading claims: don't claim outcomes you can't evidence ('guaranteed tax savings'), don't use testimonials you can't verify, and 'free' must mean free (the health check genuinely is). The [NIMA TO PROVIDE] placeholders must be replaced with real proof or removed before driving paid traffic.",
          "Not a tax agent (confirmed): Clarus Partners will not register as a tax or BAS agent - keep the 'we prepare, your registered agent lodges' framing in every ad and page, and never advertise tax agent or BAS agent services.",
        ],
      },
    ],
  },
];
